import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import styles from "../../styles/shap_of_you.module.scss";
import win from "../../assets/images/win.png";
import { PermissionDeniedScreenProps } from "./types";

const PermissionDeniedScreen: React.FC<PermissionDeniedScreenProps> = ({
  isAuthenticated,
  onExit,
}) => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  // Handle "Restart Experience" button click with consent checking
  const handleRestartExperience = async () => {
    setLoading(true);

    try {
      if (isAuthenticated) {
        // User is authenticated - check consent status via API
        const { data } = await httpClient.get(endpoints.auth.getConsentForm());

        if (data.statusText === "accepted") {
          // Scenario 1: Consent already accepted - skip TC, go to welcome
          console.log("Consent already accepted, skipping TC screen");
          localStorage.setItem("pendingNavigation", "welcome");
          if (onExit) {
            onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
          }
        } else {
          // Scenario 2: Consent rejected - show TC screen
          localStorage.setItem("pendingNavigation", "tc");
          if (onExit) {
            onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
          }
        }
      } else {
        // Scenario 3: User is NOT authenticated - go to TC without API call
        localStorage.setItem("pendingNavigation", "tc");
        if (onExit) {
          onExit(); // Exit PhotoCaptureFlow, main page will handle navigation
        }
      }
    } catch (error) {
      console.error("Error checking consent status:", error);
      // If API fails, show TC screen anyway
      localStorage.setItem("pendingNavigation", "tc");
      if (onExit) {
        onExit();
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle "Close Window" button click
  const handleCloseWindow = () => {
    router.push("/");
  };

  return (
    <div className="d-md-none w-100">
      <div className={styles.wrapper_Permission}>
        {/* Illustration */}
        <Image src={win} alt="Exit" className={styles.image} />

        {/* Title */}
        <h2>
          Permission <br /> Denied
        </h2>

        {/* Subtitle */}
        <p>Oops! We need your permission to bring this experience to life.</p>

        {/* Restart Button */}
        <button
          className={styles.restartBtn}
          onClick={handleRestartExperience}
          disabled={loading}
          style={
            loading
              ? {
                  background: "#d3d3d3",
                  color: "#888",
                  cursor: "not-allowed",
                  opacity: 0.6,
                }
              : {}
          }
        >
          <span className={styles.icon}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.46257 4.43262C7.21556 2.91688 9.5007 2 12 2C17.5228 2 22 6.47715 22 12C22 14.1361 21.3302 16.1158 20.1892 17.7406L17 12H20C20 7.58172 16.4183 4 12 4C9.84982 4 7.89777 4.84827 6.46023 6.22842L5.46257 4.43262ZM18.5374 19.5674C16.7844 21.0831 14.4993 22 12 22C6.47715 22 2 17.5228 2 12C2 9.86386 2.66979 7.88416 3.8108 6.25944L7 12H4C4 16.4183 7.58172 20 12 20C14.1502 20 16.1022 19.1517 17.5398 17.7716L18.5374 19.5674Z"
                fill={loading ? "#888" : "white"}
              />
            </svg>
          </span>
          {loading ? "Loading..." : "Restart Experience"}
        </button>

        {/* Close */}
        <button
          className={styles.closeBtn}
          onClick={handleCloseWindow}
          disabled={loading}
        >
          ✕ Close Window
        </button>
      </div>
    </div>
  );
};

export default PermissionDeniedScreen;
